import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useDismissErrorToastsOnNavigate } from '@/hooks/useDismissErrorToastsOnNavigate'
import { AuthLayout } from './layouts/AuthLayout'
import { DashboardLayout } from './layouts/DashboardLayout'
import { LoginPage } from './pages/auth/LoginPage'
import { IntegrationsPage } from './pages/integrations/IntegrationsPage'
import {
  SettingsProfilePage,
  SettingsBankingPage,
  SettingsCategoriesPage,
  SettingsUsersPage,
  SettingsFacilitiesPage,
  FacilityFinancialAccountsPage,
  ConnectionAccountsPage,
  ManualBankPage,
  SettingsOrganizationPage,
  SettingsPacksPage,
} from './pages/settings'
import { SageIntacctPage } from './pages/settings/SageIntacctPage'
// Banking pages
import {
  BankingDashboardPage,
  TransactionsImportPage,
  CrossCheckingPage,
  BalanceImportPage,
  OutstandingChecksPage,
  ProjectionsPage,
  ProjectionImportPage,
  LocAvailabilityPage,
  CreditCardAvailabilityPage,
} from './pages/banking'
// Cash Position page
import { CashPositionPage } from './pages/cash-position'
// Admin pages
import { InvitationsPage, OrganizationsPage, OrganizationDetailPage } from './pages/admin'
// Public pages
import { AcceptInvitationPage } from './pages/AcceptInvitationPage'
import { PlaidOAuthCallbackPage } from './pages/PlaidOAuthCallbackPage'
import { LedgerSyncCallbackPage } from './pages/LedgerSyncCallbackPage'
import { RequireMFA } from './components/auth/RequireMFA'
import { RequirePermission } from './components/auth/RequirePermission'
import { RequireAdminPanel } from './components/auth/RequireAdminPanel'
import { LegacyFacilityBankingRedirect } from './components/financial-accounts/LegacyFacilityBankingRedirect'

/**
 * Redirects the retired `/settings/banking*` paths onto
 * `/settings/financial-accounts*` (DCP-324 rename), preserving whatever follows
 * the prefix — so the per-connection route keeps its `:connectionId` without
 * this component having to know the shape of the tail.
 *
 * WHY A REDIRECT AND NOT A CLEAN CUT. Two callers hold the OLD path in places
 * this deploy cannot reach:
 *   * the LedgerSync connect flow persists a `returnPath` in the browser
 *     (`getLedgerSyncOAuthState`) BEFORE handing the user to the hosted widget.
 *     A session that starts before the deploy comes back afterwards, reads the
 *     stored `/settings/banking`, and would land on a route that no longer
 *     exists. (The vendor's own redirect target is unaffected — that is the
 *     static `/ledgersync-callback`, set by LEDGERSYNC_REDIRECT_URI server-side.)
 *   * bookmarks and links already shared by users.
 *
 * `replace` so the dead path does not sit in history behind the live one.
 */
function LegacyBankingSettingsRedirect() {
  const location = useLocation()
  const to = location.pathname.replace('/settings/banking', '/settings/financial-accounts')
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!user) {
    // Preserve the attempted URL so LoginPage / AuthLayout can return the user
    // to their target route after a successful sign-in. Only forward the URL
    // triplet — forwarding the full `location` would also carry `location.state`
    // (nested state from prior navigations, potentially sensitive) and the
    // opaque `location.key`, which AuthLayout never consumes.
    return (
      <Navigate
        to="/login"
        replace
        state={{
          from: {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
          },
        }}
      />
    )
  }

  return <>{children}</>
}

export default function App() {
  // Error toasts no longer close themselves (DCP-365), so they have to be
  // cleared when the user leaves the screen that raised them. Lives here rather
  // than in main.tsx because it reads the router's location, and App is the
  // outermost component inside <BrowserRouter>.
  useDismissErrorToastsOnNavigate()

  return (
    <Routes>
      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      {/* Public routes (no auth required) */}
      <Route element={<AuthLayout />}>
        <Route path="/invite/accept/:token" element={<AcceptInvitationPage />} />
      </Route>

      {/* Plaid OAuth callback — protected but without dashboard layout */}
      <Route
        path="/oauth-callback"
        element={
          <ProtectedRoute>
            <RequireMFA>
              <PlaidOAuthCallbackPage />
            </RequireMFA>
          </ProtectedRoute>
        }
      />

      {/* LedgerSync Connect widget callback — protected, no dashboard layout */}
      <Route
        path="/ledgersync-callback"
        element={
          <ProtectedRoute>
            <RequireMFA>
              <LedgerSyncCallbackPage />
            </RequireMFA>
          </ProtectedRoute>
        }
      />

      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/banking/dashboard" replace />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        {/* Settings routes — per-page product-role gates (profile is unrestricted) */}
        <Route path="/settings">
          <Route index element={<Navigate to="profile" replace />} />
          <Route path="profile" element={<SettingsProfilePage />} />
          <Route
            path="financial-accounts"
            element={
              <RequirePermission permission="manage_settings">
                <RequireMFA><SettingsBankingPage /></RequireMFA>
              </RequirePermission>
            }
          />
          <Route
            path="categories"
            element={
              <RequirePermission permission="manage_settings">
                <SettingsCategoriesPage />
              </RequirePermission>
            }
          />
          <Route
            path="users"
            element={
              <RequirePermission permission="manage_users">
                <SettingsUsersPage />
              </RequirePermission>
            }
          />
          {/* DCP-324: one connection + its accounts. Nested under
              `financial-accounts` because a connection is org-level, and
              MFA-gated like the Financial Account Settings page it is reached
              from. */}
          <Route
            path="financial-accounts/connections/:connectionId"
            element={
              <RequirePermission permission="manage_settings">
                <RequireMFA><ConnectionAccountsPage /></RequireMFA>
              </RequirePermission>
            }
          />
          {/* One manual bank (accounts sharing an institution name) and its
              accounts, reached from the Manual Financial Accounts list. The
              name is the URL-encoded segment. */}
          <Route
            path="financial-accounts/manual/:institutionName"
            element={
              <RequirePermission permission="manage_settings">
                <RequireMFA><ManualBankPage /></RequireMFA>
              </RequirePermission>
            }
          />
          {/* The manual accounts with no institution name. Its own path: a
              reserved name under `manual/` could collide with a real bank's. */}
          <Route
            path="financial-accounts/manual-unnamed"
            element={
              <RequirePermission permission="manage_settings">
                <RequireMFA><ManualBankPage /></RequireMFA>
              </RequirePermission>
            }
          />
          {/* Retired paths. The splat matches `banking` itself and anything
              below it, so one route covers both the page and its per-connection
              child. Declared AFTER the live routes it forwards to. */}
          <Route path="banking/*" element={<LegacyBankingSettingsRedirect />} />
          <Route
            path="facilities"
            element={
              <RequirePermission permission="manage_settings">
                <SettingsFacilitiesPage />
              </RequirePermission>
            }
          />
          {/* One facility's Financial Accounts: the same screens as
              `financial-accounts/*` above, narrowed to the facility. */}
          <Route
            path="facilities/:facilityId/financial-accounts"
            element={
              <RequirePermission permission="manage_settings">
                <RequireMFA><FacilityFinancialAccountsPage /></RequireMFA>
              </RequirePermission>
            }
          />
          <Route
            path="facilities/:facilityId/financial-accounts/connections/:connectionId"
            element={
              <RequirePermission permission="manage_settings">
                <RequireMFA><ConnectionAccountsPage /></RequireMFA>
              </RequirePermission>
            }
          />
          <Route
            path="facilities/:facilityId/financial-accounts/manual/:institutionName"
            element={
              <RequirePermission permission="manage_settings">
                <RequireMFA><ManualBankPage /></RequireMFA>
              </RequirePermission>
            }
          />
          <Route
            path="facilities/:facilityId/financial-accounts/manual-unnamed"
            element={
              <RequirePermission permission="manage_settings">
                <RequireMFA><ManualBankPage /></RequireMFA>
              </RequirePermission>
            }
          />
          <Route path="facilities/:facilityId/banking/*" element={<LegacyFacilityBankingRedirect />} />
          {/* Organization settings — self-guarded by canViewOrganizationSettings
              (admits super_admin, org_owner, system admin, product admin).
              No RequirePermission wrapper so org_owner (productRole=null) is
              not blocked before reaching the page-level guard. DCP-158 */}
          <Route
            path="packs"
            element={
              <RequirePermission permission="manage_settings">
                <SettingsPacksPage />
              </RequirePermission>
            }
          />
          <Route
            path="organization"
            element={<SettingsOrganizationPage />}
          />
          {/* Per-provider integration configuration pages live directly under /settings. */}
          <Route path="sage" element={<RequireMFA><SageIntacctPage /></RequireMFA>} />
        </Route>

        {/* Banking routes — gated on view_banking */}
        <Route
          path="/banking"
          element={
            <RequirePermission permission="view_banking">
              {/* Outlet renders the matched child route */}
              <Outlet />
            </RequirePermission>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<BankingDashboardPage />} />
          <Route path="cross-checking" element={<CrossCheckingPage />} />
          <Route path="transactions-import" element={<TransactionsImportPage />} />
          <Route path="balance-import" element={<BalanceImportPage />} />
          <Route path="outstanding-checks" element={<OutstandingChecksPage />} />
          <Route path="loc-availability" element={<LocAvailabilityPage />} />
          {/* DCP-374: sits directly below LOC Availability, under the same
              view_banking gate as every other Banking route. */}
          <Route path="credit-card-availability" element={<CreditCardAvailabilityPage />} />

        </Route>

        {/* Projections routes — gated on view_projections */}
        <Route
          path="/projections"
          element={
            <RequirePermission permission="view_projections">
              {/* Outlet renders the matched child route */}
              <Outlet />
            </RequirePermission>
          }
        >
          <Route index element={<Navigate to="input" replace />} />
          <Route path="input" element={<ProjectionsPage />} />
          <Route path="projections-import" element={<ProjectionImportPage />} />
        </Route>

        {/* Cash Position route — lives under the /projections URL prefix (it's
            part of the Projections section in the sidebar) but is intentionally
            NOT nested inside the /projections route group: that group is gated
            on `view_projections`, whereas Cash Position is gated on the broader
            `view_cash_position` (e.g. accounts_payable has the latter, not the
            former). Keeping it a standalone route preserves that permission
            split. */}
        <Route
          path="/projections/cash-position"
          element={
            <RequirePermission permission="view_cash_position">
              <CashPositionPage />
            </RequirePermission>
          }
        />

        {/* Admin routes — system-role gated. Exposes cross-tenant data
            (invitations, organizations) so it must stay reachable only to
            platform staff (`super_admin`, `admin`), not to product `admin`
            which is per-org. Mirrors the sidebar's `canAccessAdmin` check. */}
        <Route
          path="/admin"
          element={
            <RequireAdminPanel>
              <Outlet />
            </RequireAdminPanel>
          }
        >
          <Route index element={<Navigate to="invitations" replace />} />
          <Route path="invitations" element={<InvitationsPage />} />
          <Route path="organizations" element={<OrganizationsPage />} />
          <Route path="organizations/:id" element={<OrganizationDetailPage />} />
        </Route>
      </Route>
    </Routes>
  )
}